import { EPropertyType } from 'components/EditMetadata/EditMetadata.types';
import { AnnotationNoteValue } from 'components/HotTables/HotTables.types';
import { NoteCollectionReturn } from 'neurostore-typescript-sdk';
import { IAnalysesSelection } from 'pages/MetaAnalysis/components/CreateMetaAnalysisSpecificationDialogBase.types';
import { IAutocompleteObject } from 'components/NeurosynthAutocomplete/NeurosynthAutocomplete';
import { DEFAULT_REFERENCE_DATASETS, MULTIGROUP_ALGORITHMS } from './SelectAnalysesComponent.types';

export const LARGE_ALE_SUBTRACTION_REFERENCE_STUDY_COUNT = 1000;
export const NEUROSTORE_REFERENCE_DATASET_ID = 'neurostore';

// TODO: instead of hardcoding this, we should add a property to the meta-analysis spec with something like: "multi-group" or something similar

export const isMultiGroupAlgorithm = (estimator: IAutocompleteObject | null | undefined) => {
    if (!estimator) return false;
    return MULTIGROUP_ALGORITHMS.some((multigroupAlgorithm) => estimator.label === multigroupAlgorithm);
};

export const getFilteredAnnotationNotes = (
    annotationNotes: NoteCollectionReturn[],
    selectedValue: IAnalysesSelection | undefined
): NoteCollectionReturn[] => {
    if (!annotationNotes || !selectedValue || !selectedValue.selectionKey) return [];
    const { selectionKey, type, selectionValue } = selectedValue;

    switch (type) {
        case EPropertyType.STRING:
            if (!selectionValue) return [];
            return annotationNotes.filter((x) => {
                const annotationNote = x.note as { [key: string]: AnnotationNoteValue };
                return annotationNote[selectionKey] === selectionValue;
            });
        case EPropertyType.BOOLEAN:
            return annotationNotes.filter((x) => {
                const annotationNote = x.note as { [key: string]: AnnotationNoteValue };
                return annotationNote[selectionKey] === true; // for now, we only care about boolean filters. Later, the filter process will get more complicated
            });
        case EPropertyType.NONE:
        default:
            return [];
    }
};

export interface IStudyAnalysesTableFormat {
    studyName: string;
    studyId: string;
    analyses: { analysisName: string; analysisId: string; isSelected: boolean }[];
}

// we can assume that the input is already sorted
export const annotationNotesToTableFormatHelper = (
    notes: NoteCollectionReturn[],
    selectedNotes: NoteCollectionReturn[]
) => {
    let currStudy = '';
    const tableFormat: IStudyAnalysesTableFormat[] = [];

    const selectedNotesMap = new Map();
    selectedNotes.forEach((note) => selectedNotesMap.set(note.analysis, note));

    [...notes]
        .sort((a, b) => {
            const firstStudyId = a.study as string;
            const secondStudyId = b.study as string;
            return firstStudyId.localeCompare(secondStudyId);
        })
        .forEach((note) => {
            if (note.study === currStudy) {
                tableFormat[tableFormat.length - 1].analyses.push({
                    analysisId: note.analysis as string,
                    analysisName: note.analysis_name as string,
                    isSelected: selectedNotesMap.has(note.analysis),
                });
            } else {
                currStudy = note.study as string;
                tableFormat.push({
                    studyName: note.study_name as string,
                    studyId: note.study as string,
                    analyses: [
                        {
                            analysisId: note.analysis as string,
                            analysisName: note.analysis_name as string,
                            isSelected: selectedNotesMap.has(note.analysis),
                        },
                    ],
                });
            }
        });

    return tableFormat;
};

const valueMatchesFilter = (value: string, filter: string | undefined) => {
    const normalizedFilter = filter?.trim().toLowerCase() ?? '';
    if (!normalizedFilter) return true;
    return value.toLowerCase().includes(normalizedFilter);
};

export const filterStudyAnalysesTable = (
    studiesList: IStudyAnalysesTableFormat[],
    studyFilter: string | undefined,
    analysisFilter: string | undefined
): IStudyAnalysesTableFormat[] => {
    return studiesList
        .filter((study) => valueMatchesFilter(study.studyName, studyFilter))
        .map((study) => ({
            ...study,
            analyses: study.analyses.filter((analysis) => valueMatchesFilter(analysis.analysisName, analysisFilter)),
        }))
        .filter((study) => study.analyses.length > 0);
};

export const selectedReferenceDatasetIsDefaultDataset = (selectedReferenceDataset: string | undefined) => {
    if (!selectedReferenceDataset) return false;

    return DEFAULT_REFERENCE_DATASETS.some((x) => x.id === selectedReferenceDataset);
};

// For specifications using default reference datasets, we can get the reference dataset from database_studyset.
// For specifications where the base selection column and reference dataset is selected by the user, we can get the reference dataset from the conditions array:
//     "conditions": [ "abc", "ghu" ]. "abc" is the base selection column, "ghu" is the reference dataset.
export const getSpecificationReferenceDataset = (
    databaseStudyset: string | null | undefined,
    conditions: ReadonlyArray<string | boolean> | null | undefined
): string | undefined => {
    if (databaseStudyset) return databaseStudyset;

    const referenceCondition = conditions?.[1];
    if (referenceCondition === undefined || referenceCondition === null) return undefined;
    return `${referenceCondition}`;
};

export const isLargeAleSubtractionReference = ({
    estimatorLabel,
    referenceDataset,
    referenceStudyCount,
}: {
    estimatorLabel: string | undefined;
    referenceDataset: string | undefined;
    referenceStudyCount: number;
}): boolean => {
    if (estimatorLabel !== 'ALESubtraction' || !referenceDataset) return false;
    if (referenceDataset === NEUROSTORE_REFERENCE_DATASET_ID) return true;
    if (selectedReferenceDatasetIsDefaultDataset(referenceDataset)) return false;
    return referenceStudyCount >= LARGE_ALE_SUBTRACTION_REFERENCE_STUDY_COUNT;
};
